/**
 * Native-aware OAuth sign-in for Capacitor iOS/Android.
 *
 * The web flow (window.location.replace → Supabase → provider → back to
 * window.location.origin) doesn't work in a native shell — there's no
 * "window.location" to navigate, and OAuth providers won't redirect to a
 * capacitor://localhost URL. We have to:
 *
 *   1. Ask Supabase for the OAuth URL (without auto-redirecting).
 *   2. Open that URL in an in-app browser tab (Safari View Controller on iOS,
 *      Chrome Custom Tabs on Android) via @capacitor/browser.
 *   3. When the OAuth provider finishes, it redirects to our custom URL
 *      scheme: pleasant://auth/callback#access_token=…&refresh_token=…
 *   4. iOS routes that scheme back into the app and fires Capacitor's
 *      appUrlOpen event (see useDeepLinks.ts).
 *   5. The deep-link handler extracts the tokens from the URL fragment and
 *      calls supabase.auth.setSession() to complete the sign-in.
 *   6. We close the in-app browser so the user lands back on the app's UI
 *      instead of the now-blank OAuth page.
 *
 * For the web build this module short-circuits to the existing Lovable
 * wrapper so nothing about the web sign-in flow changes.
 */

import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { supabase } from "@/integrations/supabase/client";

/** Single source of truth for the deep-link the OAuth provider redirects to. */
export const NATIVE_AUTH_REDIRECT = "pleasant://auth/callback";

type Provider = "google" | "apple";

interface SignInResult {
  error?: Error | null;
  /** True when we kicked off a redirect / opened an external browser tab —
   *  the caller should NOT toast "signed in" yet; auth completes via the
   *  deep-link handler. */
  redirected?: boolean;
}

/**
 * Sign in with an OAuth provider. Talks to Supabase directly on both web
 * and native — the previous version delegated to Lovable's wrapper for web,
 * which calls `/~oauth/initiate` on the current origin. That endpoint only
 * exists on Lovable's hosting; on Vercel it 404s. Real-world feedback:
 * after pointing pleasantroutes.com at Vercel, Google sign-in went to
 * `https://pleasantroutes.com/~oauth/initiate?...` and Vercel returned
 * NOT_FOUND. Going straight to supabase-js works everywhere.
 *
 * Web build: lets supabase-js handle the window.location.replace redirect.
 * Native build: skipBrowserRedirect + opens the OAuth URL in an in-app
 * Safari View Controller so the pleasant://auth/callback custom scheme
 * routes back to the app via useDeepLinks.
 */
export async function signInWithProvider(provider: Provider): Promise<SignInResult> {
  const isNative = Capacitor.isNativePlatform();

  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        // Web: redirect back to the app's origin (Vercel domain, Lovable
        // preview URL, or localhost). Native: a custom URL scheme that iOS
        // routes back into the app.
        redirectTo: isNative ? NATIVE_AUTH_REDIRECT : window.location.origin,
        // Native: don't let supabase-js try to navigate (no real
        // window.location in the Capacitor WebView). Web: let it navigate.
        skipBrowserRedirect: isNative,
      },
    });
    if (error) return { error };
    if (!isNative) {
      // supabase-js already kicked off the redirect — the browser is on
      // its way to the OAuth provider. Auth completes when the user
      // returns and supabase-js sees the tokens in the URL fragment.
      return { redirected: true };
    }
    if (!data?.url) {
      return { error: new Error("No OAuth URL returned from Supabase") };
    }
    await Browser.open({ url: data.url, presentationStyle: "popover" });
    return { redirected: true };
  } catch (e) {
    return { error: toError(e) };
  }
}

/**
 * Complete a deep-link auth callback. Called by useDeepLinks when a URL
 * matching `pleasant://auth/...` lands. Extracts the access + refresh tokens
 * from the URL fragment (Supabase puts them there per the OAuth implicit
 * flow), then primes the local Supabase session.
 *
 * Returns true when the URL was recognised + tokens were applied. The deep-
 * link handler can use that to decide whether to also close the in-app
 * browser tab.
 */
export async function handleAuthDeepLink(rawUrl: string): Promise<boolean> {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return false;
  }

  // Recognise either pleasant://auth/callback (custom scheme) OR the
  // https://pleasantroutes.com/auth/callback Universal Link form (in case
  // we later switch the redirect to https for App Store polish).
  const isAuthCallback =
    /^auth(\/callback)?\/?$/.test(parsed.pathname.replace(/^\//, "")) ||
    parsed.host === "auth";
  if (!isAuthCallback) return false;

  // OAuth tokens come back in the URL fragment, not the query string.
  // Format: #access_token=…&refresh_token=…&token_type=bearer&type=signup
  const fragment = (parsed.hash || "").replace(/^#/, "");
  const params = new URLSearchParams(fragment);
  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");
  const errorDescription = params.get("error_description") ?? params.get("error");

  if (errorDescription) {
    // Surface the provider's error to the console; the calling UI should
    // also toast a friendly message.
     
    console.warn("[auth] OAuth callback error:", errorDescription);
    await closeBrowserSafely();
    return true;
  }
  if (!accessToken || !refreshToken) {
    return false;
  }

  try {
    await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
  } catch (e) {
     
    console.warn("[auth] setSession failed:", e);
  }
  await closeBrowserSafely();
  return true;
}

async function closeBrowserSafely(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Browser.close();
  } catch {
    /* already closed */
  }
}

function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === "string") return new Error(value);
  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error("Unknown auth error");
  }
}
